import { SUPPORTED_EXCHANGES } from "../../constants/supported-cex";
import { DecimalSchema } from "../../decimal";
import { QUEST_FREQUENCY, QUEST_REWARD_TYPE, QUEST_SOCIAL, QUEST_TYPE, SPINNING_WHEEL_TYPE } from "./quest.enums";
import { z } from "zod";

export const SocialConnectionMetadataSchema = z.object({
  platform: z.nativeEnum(QUEST_SOCIAL),
  platformUserId: z.string().nullable(),
  platformUsername: z.string().nullable(),
  connectedAt: z.coerce.date(),
});
export type SocialConnectionMetadata = z.infer<typeof SocialConnectionMetadataSchema>;

// Trading Progress Metadata
export const TradingMetadataSchema = z.object({
  currentVolume: DecimalSchema,
  tradesCount: z.number().nonnegative().default(0),
  lastTradeAt: z.coerce.date().optional(),
});
export type TradingMetadata = z.infer<typeof TradingMetadataSchema>;

// CEX Connection Progress Metadata
export const CexConnectionMetadataSchema = z.object({
  exchange: z.nativeEnum(SUPPORTED_EXCHANGES),
  exchangeUserId: z.string().nullable(),
  connectedAt: z.coerce.date(),
});
export type CexConnectionMetadata = z.infer<typeof CexConnectionMetadataSchema>;

// TMA TON Connection Progress Metadata
export const TmaTonConnectionMetadataSchema = z.object({
  createdAt: z.coerce.date(),
});
export type TmaTonConnectionMetadata = z.infer<typeof TmaTonConnectionMetadataSchema>;

export const TmaFirstCexConnectionMetadataSchema = z.object({
  exchange: z.nativeEnum(SUPPORTED_EXCHANGES),
  exchangeUserId: z.string().nullable(),
  connectedAt: z.coerce.date(),
});
export type TmaFirstCexConnectionMetadata = z.infer<typeof TmaFirstCexConnectionMetadataSchema>;

export const SpinningWheelMetadataSchema = z.object({
  spinningWheelType: z.nativeEnum(SPINNING_WHEEL_TYPE),
  rewardCount: z.number(),
  rewardType: z.nativeEnum(QUEST_REWARD_TYPE),
});
export type SpinningWheelMetadata = z.infer<typeof SpinningWheelMetadataSchema>;

export const UserAffiliatedMetadataSchema = z.object({
  exchange: z.nativeEnum(SUPPORTED_EXCHANGES),
  exchangeUserId: z.string().nullable(),
  connectedAt: z.coerce.date(),
});
export type UserAffiliatedMetadata = z.infer<typeof UserAffiliatedMetadataSchema>;

export const TwitterLikePostMetadataSchema = z.object({
  postId: z.string(),
});
export type TwitterLikePostMetadata = z.infer<typeof TwitterLikePostMetadataSchema>;

export const TwitterRetweetPostMetadataSchema = z.object({
  postId: z.string(),
});
export type TwitterRetweetPostMetadata = z.infer<typeof TwitterRetweetPostMetadataSchema>;

export const TwitterFollowAccountMetadataSchema = z.object({
  accountId: z.string(),
});
export type TwitterFollowAccountMetadata = z.infer<typeof TwitterFollowAccountMetadataSchema>;

export const TwitterCommentPostMetadataSchema = z.object({
  campaignName: z.string().optional().nullable(),
  partnerName: z.string().optional().nullable(),
  XAccountUsernameToFollow: z.string().optional().nullable(),
  XAccountIdToFollow: z.string().optional().nullable(),
  XPostIdToComment: z.string(),
});
export type TwitterCommentPostMetadata = z.infer<typeof TwitterCommentPostMetadataSchema>;

export const TwitterSendPostMetadataSchema = z.object({
  campaignName: z.string().optional().nullable(),
  partnerName: z.string().optional().nullable(),
  XAccountUsernameToFollow: z.string().optional().nullable(),
  XAccountIdToFollow: z.string().optional().nullable(),
});
export type TwitterSendPostMetadata = z.infer<typeof TwitterSendPostMetadataSchema>;

export const KycAndTradingMetadataSchema = z.object({
  steps: z.object({
    kycConnection: z.object({
      completed: z.boolean().default(false),
      exchange: z.nativeEnum(SUPPORTED_EXCHANGES).optional(),
      exchangeUserId: z.string().nullable().optional(),
      connectedAt: z.coerce.date().optional(),
    }),
    tradingVolume: z.object({
      currentVolume: DecimalSchema,
      tradesCount: z.number().nonnegative(),
      lastTradeAt: z.coerce.date().optional(),
    }),
  }),
});
export type KycAndTradingMetadata = z.infer<typeof KycAndTradingMetadataSchema>;

export const ArenaMatchesMetadataSchema = z.object({
  matchCount: z.number().int().nonnegative().default(0),
  lastMatchAt: z.coerce.date().optional(),
  /** `campaignName:epochId` of every competition already counted — one credit per settlement. */
  creditedMatchKeys: z.array(z.string()).default([]),
});
export type ArenaMatchesMetadata = z.infer<typeof ArenaMatchesMetadataSchema>;

// Combined metadata schema with discriminated union
export const ArenaBuyInVolumeMetadataSchema = z.object({
  /** Settled buy-in volume so far, in USDT. */
  currentVolumeUsdt: DecimalSchema,
  lastMatchAt: z.coerce.date().optional(),
  /** `campaignName:epochId` of every settled competition already counted. */
  creditedMatchKeys: z.array(z.string()).default([]),
});
export type ArenaBuyInVolumeMetadata = z.infer<typeof ArenaBuyInVolumeMetadataSchema>;

export const QuestProgressMetadataSchema = z.discriminatedUnion("questType", [
  z.object({
    questType: z.literal(QUEST_TYPE.SOCIAL_CONNECTION),
    data: SocialConnectionMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.TRADING_VOLUME),
    data: TradingMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.CEX_CONNECTION),
    data: CexConnectionMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.TMA_TON_CONNECTION),
    data: TmaTonConnectionMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.TMA_FIRST_CEX_CONNECTION),
    data: TmaFirstCexConnectionMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.SPINNING_WHEEL),
    data: SpinningWheelMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.USER_AFFILIATED),
    data: UserAffiliatedMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.TWITTER_LIKE_POST),
    data: TwitterLikePostMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.TWITTER_RETWEET_POST),
    data: TwitterRetweetPostMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.TWITTER_FOLLOW_ACCOUNT),
    data: TwitterFollowAccountMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.TWITTER_COMMENT_POST),
    data: TwitterCommentPostMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.TWITTER_SEND_POST),
    data: TwitterSendPostMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.KYC_AND_TRADING),
    data: KycAndTradingMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.ARENA_MATCHES),
    data: ArenaMatchesMetadataSchema,
  }),
  z.object({
    questType: z.literal(QUEST_TYPE.ARENA_BUY_IN_VOLUME),
    data: ArenaBuyInVolumeMetadataSchema,
  }),
]);
export type QuestProgressMetadata = z.infer<typeof QuestProgressMetadataSchema>;

export const QuestProgressSchema = z.object({
  id: z.string(),
  hubUserId: z.number(),
  questId: z.number(),
  questFrequency: z.nativeEnum(QUEST_FREQUENCY),
  questType: z.nativeEnum(QUEST_TYPE),
  progress: DecimalSchema,
  metadata: QuestProgressMetadataSchema,
  isCompleted: z.boolean().default(false),
  completedAt: z.coerce.date().optional(),
  lastProgressAt: z.coerce.date(),
  periodStart: z.coerce.date(),
  periodEnd: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type QuestProgress = z.infer<typeof QuestProgressSchema>;

export function isTradingMetadata(
  metadata: QuestProgressMetadata,
): metadata is { questType: "trading_volume"; data: TradingMetadata } {
  return metadata.questType === QUEST_TYPE.TRADING_VOLUME;
}

export function isSpinningWheelMetadata(
  metadata: QuestProgressMetadata,
): metadata is { questType: "spinning_wheel"; data: SpinningWheelMetadata } {
  return metadata.questType === QUEST_TYPE.SPINNING_WHEEL;
}
